/**
 * V217 (§1ter, D16) — /platform → tenant → "Titular (soporte)". Cuando el titular perdió el
 * acceso o su cuenta está comprometida, soporte inicia la transferencia a otro SUPER_USER activo
 * (motivo obligatorio, ≥ 10 caracteres). Queda PENDIENTE hasta que el destinatario la acepta desde
 * su sesión. La casilla "congelar al titular actual" cierra sus sesiones y bloquea sus acciones
 * exclusivas hasta que el nuevo titular acepte o soporte descongele (también con motivo).
 * Formulario sin estado de red (testeable con renderToStaticMarkup); el contenedor carga y envía.
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';

export interface OwnerSupportInfo {
  owner: { userId: string; name: string; email: string };
  frozen: boolean;
  frozenReason: string | null;
  pending: { toName: string | null; expiresAt: string | null } | null;
  candidates: Array<{ userId: string; email: string; name: string }>;
}

export const MIN_REASON = 10;
export const reasonOk = (r: string) => r.trim().length >= MIN_REASON;

const input = 'w-full rounded-lg border border-slate-200 px-2.5 py-2 text-sm';
const btn = 'w-full rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 sm:w-auto';

export function OwnerSupportForm({
  info, busy, error, okMsg, onTransfer, onUnfreeze,
  initial = { to: '', reason: '', freeze: false, unfreezeReason: '' },
}: {
  info: OwnerSupportInfo;
  busy: boolean;
  error?: string | null;
  okMsg?: string | null;
  onTransfer: (v: { newOwnerUserId: string; reason: string; freezeOwner: boolean }) => void;
  onUnfreeze: (reason: string) => void;
  initial?: { to: string; reason: string; freeze: boolean; unfreezeReason: string };
}) {
  const [to, setTo] = useState(initial.to || info.candidates[0]?.userId || '');
  const [reason, setReason] = useState(initial.reason);
  const [freeze, setFreeze] = useState(initial.freeze);
  const [unfreezeReason, setUnfreezeReason] = useState(initial.unfreezeReason);
  const fecha = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('es-MX') : '');

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-600">
        Titular actual: <strong className="text-slate-800">{info.owner.name}</strong> · {info.owner.email}
      </p>
      {info.frozen && (
        <p className="rounded-lg bg-rose-50 px-2 py-1.5 text-xs text-rose-800">
          Titularidad <strong>congelada</strong>{info.frozenReason ? `: ${info.frozenReason}` : ''}.
        </p>
      )}
      {info.pending && (
        <p className="rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
          Transferencia pendiente a <strong>{info.pending.toName || 'otro administrador'}</strong>
          {info.pending.expiresAt ? ` hasta el ${fecha(info.pending.expiresAt)}` : ''}. Se hace efectiva cuando acepta desde su sesión.
        </p>
      )}

      {info.candidates.length === 0 ? (
        <p className="text-xs text-slate-500">No hay otro administrador activo en este tenant para recibir la titularidad.</p>
      ) : (
        <div className="space-y-2">
          <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-400" htmlFor="owner-to">Nuevo titular</label>
          <select id="owner-to" className={input} value={to} onChange={(e) => setTo(e.target.value)}>
            {info.candidates.map((c) => (
              <option key={c.userId} value={c.userId}>{c.name || c.email} · {c.email}</option>
            ))}
          </select>
          <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-400" htmlFor="owner-reason">Motivo (obligatorio, mínimo {MIN_REASON} caracteres)</label>
          <textarea id="owner-reason" className={input} rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={freeze} onChange={(e) => setFreeze(e.target.checked)} />
            <span>Congelar al titular actual (cuenta comprometida): se cierran sus sesiones y se bloquean sus acciones de titular hasta que el nuevo acepte.</span>
          </label>
          <button
            type="button"
            className={`${btn} bg-slate-900 text-white`}
            disabled={busy || !to || !reasonOk(reason)}
            onClick={() => onTransfer({ newOwnerUserId: to, reason: reason.trim(), freezeOwner: freeze })}
          >
            {busy ? 'Enviando…' : 'Cambiar titular (soporte)'}
          </button>
        </div>
      )}

      {info.frozen && (
        <div className="space-y-2 border-t border-slate-100 pt-3">
          <label className="block text-[11px] uppercase tracking-[0.14em] text-slate-400" htmlFor="owner-unfreeze">Motivo para descongelar</label>
          <textarea id="owner-unfreeze" className={input} rows={2} value={unfreezeReason} onChange={(e) => setUnfreezeReason(e.target.value)} />
          <button
            type="button"
            className={`${btn} bg-white text-slate-700 ring-1 ring-slate-300`}
            disabled={busy || !reasonOk(unfreezeReason)}
            onClick={() => onUnfreeze(unfreezeReason.trim())}
          >
            Descongelar
          </button>
        </div>
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
      {okMsg && <p className="text-xs text-emerald-700">{okMsg}</p>}
    </div>
  );
}

/** Contenedor: carga GET /platform/tenants/:id/owner y envía las acciones. */
export function OwnerSupportPanel({ tenantId }: { tenantId: string }) {
  const [info, setInfo] = useState<OwnerSupportInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [rev, setRev] = useState(0);

  const load = useCallback(async () => {
    try { setInfo(await api<OwnerSupportInfo>(`/platform/tenants/${tenantId}/owner`)); }
    catch (e: any) { setError(e?.body?.message || e?.message || 'No se pudo cargar el titular.'); }
  }, [tenantId]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<unknown>, msg: string) => {
    setBusy(true); setError(null); setOkMsg(null);
    try { await fn(); setOkMsg(msg); await load(); setRev((r) => r + 1); }
    catch (e: any) {
      const m = e?.body?.message;
      setError(Array.isArray(m) ? m.join(' · ') : (m || e?.message || 'No se pudo completar.'));
    } finally { setBusy(false); }
  };

  if (!info) return <p className="text-xs text-slate-400">{error ?? 'Cargando…'}</p>;
  return (
    <OwnerSupportForm
      key={rev}
      info={info} busy={busy} error={error} okMsg={okMsg}
      onTransfer={(v) => run(() => api(`/platform/tenants/${tenantId}/owner`, { method: 'POST', body: v }),
        v.freezeOwner
          ? 'Transferencia pendiente; el titular actual quedó congelado. Avisamos a los dos por correo.'
          : 'Transferencia pendiente. Avisamos a los dos por correo.')}
      onUnfreeze={(reason) => run(() => api(`/platform/tenants/${tenantId}/owner/unfreeze`, { method: 'POST', body: { reason } }),
        'Titularidad descongelada. Avisamos por correo.')}
    />
  );
}
